/**
 * Shared Type Definitions for Partial Design Generator
 * 
 * ==============================================================================
 * COORDINATE SYSTEM & UNIT CONVENTIONS
 * ==============================================================================
 * 
 * | Parameter     | DB Format (SceneData)           | LLM / Python Format (RoomLayoutDb)  | Discriminator Format (ValidationRequest) |
 * |---------------|---------------------------------|--------------------------------------|-------------------------------------------|
 * | Width Axis    | X (horizontal)                  | X (horizontal)                       | X (horizontal)                            |
 * | Length Axis   | Z (horizontal)                  | Y (horizontal)                       | Z (horizontal)                            |
 * | Height Axis   | Y (vertical)                    | Z (vertical)                         | Y (vertical)                              |
 * | Origin        | Center of room floor (0, 0, 0)  | Bottom-left corner (0, 0, 0)         | Center of room floor (0, 0, 0)            |
 * | Length Units  | Meters (m)                      | Meters (m)                           | Millimeters (mm)                          |
 * | Size Units    | Millimeters (mm)                | Meters (m)                           | Millimeters (mm)                          |
 * | Rotation Axis | Y-axis                          | Z-axis                               | Y-axis                                    |
 * | Rotation Units| Radians (rad)                   | Degrees (deg, 0-360 CCW)             | Radians (rad)                             |
 * 
 * ==============================================================================
 * IDENTIFIER (ID) CONVENTIONS
 * ==============================================================================
 * 
 * 1. Room ID:
 *    - Located at `Room.id`. This is a database UUID string.
 * 2. Instance ID (within a layout):
 *    - In the DB: `SceneObject._id` is a unique number (e.g., 1, 2, 3...) within the scene.
 *    - In the AI: `RoomLayoutDb.items[].itemId` is the string representation of `SceneObject._id`.
 * 3. Accessory/Catalog ID:
 *    - In the DB: `SceneObject._accesoryId` is a UUID string referencing the furniture catalog template.
 *    - In the AI: stored inside `metadata.accesoryId` of `RoomLayoutDb.items` to prevent data loss.
 * 4. Snapping Parent ID:
 *    - In the DB: `SceneObject._parentId` references the `_id` of the parent object.
 *    - In the AI: stored inside `metadata.parentId`.
 */

// ==============================================================================
// SECTION 1: DATABASE & CATALOG RAW FORMATS (DB CLIENT)
// ==============================================================================

/**
 * Represents the primary Room document stored in the database.
 */
export interface Room {
  /** Database UUID string for the room configuration */
  id: string;
  name: string;
  /** JSON-serialized string of SceneData */
  sceneData: string;
  userId: string;
  roomTypeId: string;
  sceneImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Parsed representation of the room scene structure used by the 3D planner tool.
 */
export interface SceneData {
  _version: string;
  _name: string;
  _rooms: SceneRoom[];
  _groups: SceneGroup[];
  _materials: SceneMaterial[];
}

/**
 * Physical room structure within the scene.
 */
export interface SceneRoom {
  /** Room dimensions in METERS (X: width, Y: height, Z: length) */
  _size: Vector3D;
  /** Room position in METERS relative to world origin */
  _position: Vector3D;
  _rotation: Vector3D;
  /** List of placed furniture items */
  _objects: SceneObject[];
  _floor: string;
  _walls: SceneWallColors;
}

/**
 * Placed furniture/accessory instance within a SceneRoom.
 */
export interface SceneObject {
  /** Unique numeric layout instance ID (e.g. 1, 2, 3...) */
  _id: number;
  /** UUID reference pointing to the accessory/furniture catalog */
  _accesoryId: string;
  /** Reference to parent instance ID for attachment/snapping (0 if none) */
  _parentId: number;
  _name: string;
  /** Instance position in METERS relative to the room center floor (0,0,0) */
  _position: Vector3D;
  /** Euler rotation in RADIANS around Y-axis (or Quaternion if contains w) */
  _rotation: Vector3D;
  /** Furniture dimensions in MILLIMETERS (X: width, Y: height, Z: length) */
  _size: Vector3D;
  _url: string;
  _snapType: number;
  _materials: SceneObjectMaterial[];
}

export interface Vector3D {
  x: number;
  y: number;
  z: number;
}

export interface WallColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface SceneWallColors {
  _leftWall: WallColor;
  _rightWall: WallColor;
  _frontWall: WallColor;
  _backWall: WallColor;
  _ceiling: WallColor;
}

export interface SceneObjectMaterial {
  _variant: string;
  _index: number;
}

export interface SceneMaterial {
  _variantsName: string;
  _currentSelected: number;
  _names: string[];
}

export interface SceneGroup {
  _objectIds: number[];
}

export interface AccessoryImage {
  key: string;
  isPrimary: boolean;
}

export interface CategoryAccessory {
  id: string;
  name: string;
  images: AccessoryImage[];
}

export interface AccessoryCategory {
  id: string;
  name: string;
  roomTypeId: string;
  parentId: string | null;
  previewImage: string | null;
  createdAt: string;
  updatedAt: string;
  accessories?: CategoryAccessory[];
}

export interface AccessoryVariant {
  id: string;
  accessoryId: string;
  isDefault: boolean;
  images: AccessoryImage[];
  url: string;
  price: string;
  priceLastUpdated: string;
  attributes: Array<{
    key: string;
    value: string;
    filterKey?: string;
  }>;
  textureName: string | null;
  modelName: string;
  modelMediaObjectId: string | null;
  color: string;
  dimensionsX: number;
  dimensionsY: number;
  dimensionsZ: number;
  availability: string | null;
  primary: boolean;
}

export interface AccessoryDetail {
  id: string;
  name: string;
  manufacturerId: string;
  description: string;
  modelName: string;
  modelMediaObjectId: string | null;
  modelControl: any[];
  groupModel: any | null;
  relatedAccessoryId: string | null;
  categoryId: string;
  style: string | null;
  modelDictionary: any[];
  variants: any[];
  primaryVariant: AccessoryVariant;
  createdAt: string;
  updatedAt: string;
}

export interface GetAccessoryCategoriesParams {
  parentId?: string;
  noParent?: boolean;
  roomTypeId?: string;
  page?: number;
  itemsPerPage?: number;
  pagination?: boolean;
}

export interface GetRoomsParams {
  page?: number;
  itemsPerPage?: number;
  pagination?: boolean;
}

export interface SearchAccessoriesParams {
  q?: string;
  page?: number;
  itemsPerPage?: number;
  manufacturerId?: string;
  categoryId?: string;
  color?: string;
  priceMin?: number;
  priceMax?: number;
  maxX?: number;
  maxY?: number;
  maxZ?: number;
  dimensionMatch?: 'any' | 'all';
  pagination?: boolean;
}

export interface UpdateRoomPayload {
  name?: string;
  sceneData?: string | SceneData;
  userId?: string;
  roomTypeId?: string;
  sceneImageUrl?: string | null;
}

// ==============================================================================
// SECTION 2: INTERNAL / LLM / AI NORMALIZED FORMATS
// ==============================================================================

export interface RGBColor {
  r: number;
  g: number;
  b: number;
}

/**
 * Represents the normalized layout format used internally, by the AI prompt
 * generator, and in the Python positioning executor.
 */
export interface RoomLayout {
  /** Room ID or unique identifier */
  id: string;
  name: string;
  /** Room width in METERS (X-axis) */
  width: number;
  /** Room length in METERS (Y-axis) */
  length: number;
  /** Room height in METERS (Z-axis) */
  height: number;
  /** Placed furniture items in normalized coordinate format */
  items: Array<{
    /** Instance ID of the item (holds stringified SceneObject._id) */
    itemId: string;
    label: string;
    /** Descriptive details of the furniture item, sent to the LLM for positioning context */
    description: string;
    /** English classification category (e.g. "chair", "table", "cabinet", "sofa", "bed") */
    type: string;
    /** Position in METERS relative to bottom-left corner of the room floor */
    position: {
      x: number;
      y: number;
      z: number;
    };
    /** Furniture size/dimensions in METERS */
    size: {
      width: number;
      length: number;
      height: number;
    };
    /** Rotation around Z-axis (vertical) in DEGREES (0 - 360 CCW) */
    angle: number;
    /** Color values in RGB format, sent to Python (e.g. [{r: 255, g: 0, b: 0}]) */
    colors?: RGBColor[];
    /** Material types, sent to both LLM and Python (e.g. ["wood", "leather"]) */
    materials?: string[];
    /** Assigned zone identifier (e.g. 'living-id', 'dining-id', 'workplace-id') */
    zoneId?: string | null;
    /** Stored metadata (such as original DB fields like accessory ID, parent ID) to prevent data loss */
    metadata?: Record<string, any>;
  }>;
  metadata?: Record<string, any>;
}

/**
 * Questionnaire detailing occupants and layout features.
 * Fed to the AI to customize the generated layout constraints.
 */
export interface Questionnaire {
  numberOfAdults: number;
  numberOfChildren: number;
  numberOfGuests: number;
  workplace: boolean;
  playground: boolean;
}

/**
 * Output structure returned by the Program-Aided Layout Python Executor.
 */
export interface PythonExecutorOutput {
  status: 'success' | 'error';
  error?: string;
  warnings?: string[];
  /** List of updated layout coordinates generated by the placement algorithm */
  placements: Array<{
    /** Instance ID (maps back to itemId / SceneObject._id) */
    id: string;
    /** Position in METERS relative to bottom-left corner of the room floor */
    position: {
      x: number;
      y: number;
      z: number;
    };
    /** Rotation in DEGREES around vertical Z-axis */
    rotation: number;
  }>;
  items?: Array<{
    itemId: string;
    label: string;
    description: string;
    type: string;
    position: {
      x: number;
      y: number;
      z: number;
    };
    size: {
      width: number;
      length: number;
      height: number;
    };
    angle: number;
    materials: string[];
    isNew?: boolean;
    accessoryId?: string | null;
    metadata?: Record<string, any>;
  }>;
}
